/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import _ from 'lodash';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid } from '@coursera/cds-core';
import { AssignmentCalendarItem_ProgressState as ProgressState } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';
import type { CalendarItem } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';

import { getCalendarItemsMap } from 'bundles/degree-home/components/v2/Calendar/CalendarUtils';
import { filterOptionValues } from 'bundles/degree-home/components/v2/Calendar/constants/constants';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/degree-home';

import RecentGradesEmptyState from './RecentGradesEmptyState';
import { RecentGradesItem } from './RecentGradesItem';

const styles: Record<string, Interpolation<Theme>> = {
  recentGradesItemContainer: css`
    height: auto;
    padding: 0;
    margin: 1rem 0;
  `,
  recentGradesItem: css`
    margin: 0;
  `,
};

export const RecentGradesItemsList = ({
  data,
  refetch,
}: {
  data?: { GetDegreeHomeCalendar: { calendarItems: CalendarItem[] } };
  refetch: () => void;
}) => {
  const { calendarItems, setCalendarItemsData, setRefetchCalendarItems } = useDegreeContext();

  React.useEffect(() => {
    // Ensure calendarItems get set when in V3, as this is the only place in V3 where this would be done.
    if (data?.GetDegreeHomeCalendar?.calendarItems) {
      setCalendarItemsData(getCalendarItemsMap(data.GetDegreeHomeCalendar.calendarItems));
    }
    setRefetchCalendarItems(() => refetch);
  }, [data, refetch, setCalendarItemsData, setRefetchCalendarItems]);

  const recentGradesItems = _.flatMap(calendarItems[filterOptionValues.Deadlines], (items) => items);

  const [itemsToDisplay, setItemsToDisplay] = useState(3);

  const onClickShowMore = () => {
    setItemsToDisplay(itemsToDisplay + 3);
  };

  // Filter for gradedAt items and sort
  const recentGradesList = useMemo(() => {
    const filteredRecentGradesItems = recentGradesItems.filter(
      (item) =>
        item.assignment?.gradedAtTime !== undefined &&
        item.assignment.grade !== undefined &&
        (item.assignment.progressState === ProgressState.PROGRESS_STATE_PASSED ||
          item.assignment.progressState === ProgressState.PROGRESS_STATE_FAILED)
    );
    return _.orderBy(filteredRecentGradesItems, ['assignment.gradedAtTime'], ['desc']);
  }, [recentGradesItems]);

  return (
    <div
      css={styles.recentGradesItemContainer}
      id="recentGradesItemsContainer"
      data-e2e="recent-grades-items-container"
    >
      <Grid container spacing={8}>
        <Grid container item alignItems="center" xs={12} spacing={24} css={styles.recentGradesItem}>
          {recentGradesList.length === 0 && <RecentGradesEmptyState />}
          {recentGradesList.length > 0 &&
            recentGradesList.slice(0, itemsToDisplay).map((item) => {
              if (item.assignment) {
                return <RecentGradesItem key={item.assignment.assignmentUrl} item={item.assignment} />;
              }
              return null;
            })}
        </Grid>

        {recentGradesList.length > itemsToDisplay && (
          <Grid container item>
            <Button
              data-e2e="show-more-recent-grades-items"
              variant="ghost"
              trackingName="show_more_recent_grades"
              component={TrackedButton}
              onClick={() => onClickShowMore()}
              withVisibilityTracking={false}
              requireFullyVisible={false}
            >
              {_t('Show More')}
            </Button>
          </Grid>
        )}
      </Grid>
    </div>
  );
};
